import { pollUntil } from '@/services/polling'
import { getMusicGenerationStatus, createMusicGeneration, createSongGeneration } from '@/api/endpoints/music'
import { isCopyrightError } from '@/api/errors'
import type { CreateMusicGenerationData, CreateSongGenerationData, MusicMessageObject } from '@/types'

export type GenerationKind = 'music' | 'song'

export type StartResult = { ok: true; taskId: string } | { ok: false; message: string }

export type GenerationOutcome =
	| { kind: 'success'; aiMessage: MusicMessageObject }
	| { kind: 'copyright'; message: string }
	| { kind: 'failed' }
	| { kind: 'cancelled' }

export async function startGeneration(
	kind: GenerationKind,
	payload: CreateMusicGenerationData | CreateSongGenerationData
): Promise<StartResult> {
	const response =
		kind === 'song'
			? await createSongGeneration(payload as CreateSongGenerationData)
			: await createMusicGeneration(payload as CreateMusicGenerationData)

	if (response.error) {
		if (isCopyrightError(response.error.code)) return { ok: false, message: response.error.message }
		throw new Error(response.error.message)
	}
	return { ok: true, taskId: response.task_id }
}

export async function pollGeneration(
	taskId: string,
	shouldContinue: () => boolean,
	delay = 4000
): Promise<GenerationOutcome> {
	const response = await pollUntil(() => getMusicGenerationStatus(taskId), {
		delay,
		shouldContinue,
		isPending: (r) => !r.error && r.status === 'pending',
		// Сетевые ошибки НЕ ретраим молча — пробрасываем наружу, чтобы показать InternetErrorDialog
		// и добрать результат через resume при возврате online (как в pollImageStatus).
		shouldRetry: () => false
	})

	if (!response) return { kind: 'cancelled' }

	if (response.error) {
		if (isCopyrightError(response.error.code)) return { kind: 'copyright', message: response.error.message }
		const err = new Error(response.error.message)
		;(err as { _isApiError?: boolean })._isApiError = true
		throw err
	}

	if (response.status === 'success' && response.aiMessage) {
		return { kind: 'success', aiMessage: response.aiMessage }
	}

	if (response.status === 'failed') {
		return { kind: 'failed' }
	}

	return { kind: 'cancelled' }
}
